import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Routes } from 'src/app/core/routes/routes';
import { MenuItem, MenuItemCommandEvent } from 'primeng/api';
import { BusinessStateService } from '@core/services/business-state.service';
import { PanelMenu } from 'primeng/panelmenu';
import { AuthService, UserRole } from '@core/auth/services/auth-service';

@Component({
  selector: 'app-sidebar',
  imports: [
    CommonModule,
    PanelMenu
  ],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
})
export class Sidebar {
  private router = inject(Router);
  private readonly authApi = inject(AuthService);
  private readonly businessStateService = inject(BusinessStateService);

  menuItems: MenuItem[] = [];
  activeMenuItem: string = '';
  isFormsEnabled: boolean = false;
  isEmissionSourcesEnabled: boolean = false;

  ngOnInit() {
    this.getMenuItems();

    // Hacer match entre url de vista actual e ítem del menú para marcar breadcrumb
    const currentUrl = this.router.url.replace('/', '');
    this.activeMenuItem = currentUrl;
    this.syncMenuState(this.menuItems, currentUrl);

    // Obtener nueva empresa/sede seleccionada
    this.businessStateService.selectedBusiness$.subscribe(() => {
      this.updateFormsMenuVisibility();
      this.updateEmissionSourcesMenuVisibility();
    });

    // Verificar estado inicial
    this.updateFormsMenuVisibility();
    this.updateEmissionSourcesMenuVisibility();
  }

  private syncMenuState(items: MenuItem[], currentUrl: string): boolean {
    let found = false;

    for (const item of items) {
      // Si el ítem no tiene submenú
      if (item.url === currentUrl) {
        found = true;
      }

      // Si el ítem tiene submenú
      if (item.items?.length) {
        const childActive = this.syncMenuState(item.items, currentUrl);

        // Desplegar submenú si tiene algún hijo activo
        if (childActive) {
          item.expanded = true;
          found = true;
        }
      }
    }

    return found;
  }

  /**
   * Actualiza estado disabled del ítem Formularios de acuerdo con empresa/sede seleccionada
   */
  private updateFormsMenuVisibility(): void {
    const wasFormsEnabled = this.isFormsEnabled;
    this.isFormsEnabled = this.businessStateService.isOfficeSelected();

    // Verificar si está en la vista de formularios y cambió a una selección que no es sede
    const currentUrl = this.router.url;
    const isInFormsView = currentUrl.startsWith('/form');
    const isOfficeSelected = this.businessStateService.isOfficeSelected();

    // Si cambió a una selección que no es sede, redirigir al dashboard
    if (isInFormsView && !isOfficeSelected) {
      this.router.navigate([Routes.dashboardPage]);
    }

    // Reconstruir el menú
    if (wasFormsEnabled !== this.isFormsEnabled) {
      this.getMenuItems();

      // Hacer match entre vista actual e ítem del menú después de reconstruir
      const cleanUrl = currentUrl.replace('/', '');
      this.syncMenuState(this.menuItems, cleanUrl);
    }
  }

  /**
   * Actualiza estado disabled del ítem Fuentes de emisión de acuerdo con select general de empresa
   * Deshabilitado cuando es "Todos" o "Grupo empresarial"
   * Habilitado cuando es "Empresa" o "Sede"
   */
  private updateEmissionSourcesMenuVisibility(): void {
    const wasEmissionSourcesEnabled = this.isEmissionSourcesEnabled;
    const selectedBusiness = this.businessStateService.getSelectedBusiness();
    const kind = selectedBusiness?.data?.kind?.toString().toLowerCase();
    
    // Habilitado solo cuando es 'business' u 'office'
    this.isEmissionSourcesEnabled = kind === 'business' || kind === 'office';

    // Verificar si está en la vista de fuentes de emisión y cambió a una selección no válida
    const currentUrl = this.router.url;
    const isInEmissionSourcesView = currentUrl.startsWith('/emissions/sources');
    const isValidSelection = this.isEmissionSourcesEnabled;

    // Si cambió a una selección no válida, redirigir al dashboard
    if (isInEmissionSourcesView && !isValidSelection) {
      this.router.navigate([Routes.dashboardPage]);
    }

    // Reconstruir el menú si cambió el estado
    if (wasEmissionSourcesEnabled !== this.isEmissionSourcesEnabled) {
      this.getMenuItems();

      // Hacer match entre vista actual e ítem del menú después de reconstruir
      const cleanUrl = currentUrl.replace('/', '');
      this.syncMenuState(this.menuItems, cleanUrl);
    }
  }

  getMenuItems() {
    const isBussinessmen = this.authApi.hasRole(UserRole.BUSSINESSMEN);

    this.menuItems = [
      {
        label: 'Dashboard',
        icon: 'team_dashboard',
        url: Routes.dashboardPage,
        command: (event) => this.setActiveItem(event)
      },
      {
        label: 'Usuarios',
        icon: 'person',
        items: [
          ...(!isBussinessmen ? [{
            label: 'Añadir usuario',
            url: Routes.usersAddPage,
            command: (event: any) => this.setActiveItem(event)
          }] : []),
          {
            label: 'Lista de usuarios',
            url: Routes.usersPage,
            command: (event) => this.setActiveItem(event)
          }
        ]
      },
      {
        label: 'Empresas',
        icon: 'domain',
        items: [
          {
            label: 'Lista de empresas',
            url: Routes.businessesPage,
            command: (event) => this.setActiveItem(event)
          },
          {
            label: 'Lista de sedes',
            url: Routes.officesPage,
            command: (event) => this.setActiveItem(event)
          }
        ]
      },
      {
        label: 'Emisiones',
        icon: 'energy_savings_leaf',
        items: [
          {
            label: 'Factores de emisión',
            url: Routes.emissionsFactorsPage,
            command: (event) => this.setActiveItem(event)
          },
          {
            label: 'Fuentes de emisión',
            url: Routes.emissionsSourcesPage,
            command: (event) => this.setActiveItem(event),
            disabled: !this.isEmissionSourcesEnabled
          },
          {
            label: 'Formularios',
            url: Routes.formsPage,
            command: (event: any) => this.setActiveItem(event),
            disabled: !this.isFormsEnabled
          }
        ]
      },
      {
        label: 'Reportes',
        icon: 'assignment',
        items: [
          {
            label: 'Por mes',
            url: Routes.resultsByMonthReportsPage,
            command: (event) => this.setActiveItem(event)
          },
          {
            label: 'Resultados',
            url: Routes.resultsReportsPage,
            command: (event) => this.setActiveItem(event)
          }
        ],
      }
    ];
  }

  setActiveItem(event: MenuItemCommandEvent): void {
    const item = event.item as MenuItem;
    if (item?.url) {
      this.activeMenuItem = item.url;
      this.router.navigate([item.url]);
    }
  }
}
